/**
 * Dialog — modal layer for the shell.
 *
 * Z-LEDGER (must stay in lockstep with stage-host / sheet / select / popover):
 *   TopNav / DockBar ……………… z-40
 *   AppsStage scrim ………………… z-[59]
 *   AppsStage panel ………………… z-[60]  (Chat, Play-in-stage, hosted apps)
 *   Stage tooltips ………………… z-[61]
 *   GlobalDropLayer overlay …… z-[70]  (blocks/drop-zone.tsx DropOverlay)
 *   GlobalDropLayer error …… z-[71]  (the "file too large" Alert)
 *   **Dialog / Sheet (this) …… z-[80]**  — above Stage so modals opened *from*
 *       Stage content (Add agent, Play confirmations, etc.) paint on top.
 *   Select / Popover / Dropdown … z-[90]  — above Dialog so pickers inside modals work
 *   Tooltip ………………………… z-[90]  — same reason: it portals to body, so at the
 *       old z-50 every tooltip inside a Dialog/Sheet became invisible the
 *       moment this layer moved to 80 (PeekDialog's IconTooltips, reachable
 *       anywhere via ?peek=<uid>). Raising Dialog without raising Tooltip is
 *       the regression this line exists to prevent.
 *
 * Everything above portals to document.body, which is what makes the numbers
 * comparable at all — they are siblings in the root stacking context.
 *
 * NOT on this ladder: PersonalLightbox (blocks/personal/lightbox.tsx) is NOT
 * portaled, so its z-[90]/z-[92]/z-[100] are scoped to whatever stacking
 * context its host creates and cannot be compared with the layers above. The
 * ledger used to list it as "Lightbox z-[90]+", which invited exactly that
 * false comparison. Leave it off; if it ever needs to outrank a modal, portal
 * it first, then give it a band here.
 *
 * Never leave Dialog at default z-50 while Stage is z-[60] — that is the
 * "modal opens but nothing visible" class of bug. And when you move a layer
 * here, grep for hand-rolled peers that portal (blocks/view-menu.tsx) — an
 * atom's z is not the whole story.
 */

import * as React from "react";
import { XIcon } from "./icons";
import { Dialog as DialogPrimitive } from "radix-ui";

import { cn } from "./utils";
import { SurfaceProvider } from "./surface-context";

/** Modal stack layer — above AppsStage (60), below floating menus (90). */
const MODAL_Z = "z-[80]";

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 bg-black/40 backdrop-blur-xs",
        MODAL_Z,
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  closePlacement = "inside",
  placement = "center",
  fullscreen = false,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  /**
   * Where the close affordance sits. "inside" (default) tucks the X into the
   * panel's top-right corner; "outside" floats it just off the corner as a
   * filled circular button — reads clearly against a blurred backdrop.
   */
  closePlacement?: "inside" | "outside";
  /**
   * Vertical anchoring. "center" (default) for ordinary dialogs; "top" pins
   * the panel at 12vh — use for palettes/search whose async results grow the
   * list, so the input doesn't jump as the panel reflows.
   */
  placement?: "center" | "top";
  /**
   * Edge-to-edge takeover — no modal panel chrome (no popover surface, padding,
   * rounding, max-width, or built-in close). The child fills the viewport and
   * paints its own surface (the Play "Screening Room" takeover). Keeps Radix's
   * focus trap + Escape + scroll-lock; the overlay still closes on click.
   */
  fullscreen?: boolean;
}) {
  if (fullscreen) {
    return (
      <DialogPortal data-slot="dialog-portal">
        <DialogOverlay />
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 outline-none duration-200",
            MODAL_Z,
            className,
          )}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      {/*
        Position via a fixed flex wrapper rather than a transform-based center
        on the panel itself. tw-animate-css's `enter` keyframe rewrites
        `transform` wholesale (to `translate3d(0,0,0) scale(.95)`), so a
        `translate-x/y-[-50%]` centering gets dropped for the duration of the
        open animation — the panel slides in from an offset corner and snaps
        into place. Laying the panel out with flex keeps `transform`
        exclusively for the zoom/fade, so it animates in place.
      */}
      <div
        className={cn(
          "fixed inset-0 flex justify-center overflow-y-auto p-4",
          MODAL_Z,
          placement === "top" ? "items-start pt-[12vh]" : "items-center",
        )}
      >
        {/*
          Every modal sits on the SAME surface as the command palette / search
          (the approved reference): the popover surface (surface-3), not the
          lighter surface-5 a `<Elevated offset={4}>` would paint. Painting
          `bg-popover` directly — rather than relying on substrate+offset — keeps
          every dialog identical regardless of where it's opened from, and the
          SurfaceProvider re-bases nested cards onto that surface (like a popover
          does). The shadow keeps the modal's lift; `shadow-surface-6` matches
          the palette.
        */}
        <SurfaceProvider value={3}>
          <DialogPrimitive.Content
            data-slot="dialog-content"
            className={cn(
              "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 relative grid w-full max-w-[calc(100%-2rem)] gap-4 rounded-xl bg-popover shadow-surface-6 p-6 duration-200 outline-none sm:max-w-lg",
              className,
            )}
            {...props}
          >
            {children}
            {showCloseButton &&
              (closePlacement === "outside" ? (
                <DialogPrimitive.Close
                  data-slot="dialog-close"
                  className="ring-offset-background focus:ring-ring bg-background text-muted-foreground hover:text-foreground absolute -top-3 -right-3 flex h-7 w-7 items-center justify-center rounded-full border border-border shadow-sm transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
                >
                  <XIcon />
                  <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
              ) : (
                <DialogPrimitive.Close
                  data-slot="dialog-close"
                  className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
                >
                  <XIcon />
                  <span className="sr-only">Close</span>
                </DialogPrimitive.Close>
              ))}
          </DialogPrimitive.Content>
        </SurfaceProvider>
      </div>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("type-title", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("type-subtext", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
